import { ChevronRight, Power } from 'lucide-react';
import { Button, cn } from '@staff-app/ui';
import { safeDistanceToNow } from '@/lib/safe-format';

/**
 * "On the glass" facts — port of scio3 `OnTheGlassFacts`: screen power (hdmiActive), what is playing
 * now, and whether the content has expired. The pixel proof (screenshot) lives on the Content tab;
 * this card links there.
 */
export function OnTheGlassFacts({
  hdmiActive,
  nowPlaying,
  selectionDate,
  contentExpired,
  onViewScreenshot,
}: {
  hdmiActive: boolean | null | undefined;
  nowPlaying: string | null | undefined;
  selectionDate: string | null | undefined;
  contentExpired: boolean | null | undefined;
  onViewScreenshot?: () => void;
}) {
  const since = selectionDate ? safeDistanceToNow(selectionDate, {}, '') : '';

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-lg border bg-card px-3 py-2 shadow-sm"
      data-testid="testing-on-the-glass"
    >
      <dl className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <div className="flex items-center gap-1.5">
          <dt className="text-muted-foreground">
            <Power className="mr-1 inline h-3.5 w-3.5 align-[-2px]" aria-hidden />
            {'Screen'}
          </dt>
          <dd
            className={cn(
              'font-medium',
              hdmiActive === true
                ? 'text-teal-700 dark:text-teal-400'
                : hdmiActive === false
                  ? 'text-amber-700 dark:text-amber-500'
                  : 'text-muted-foreground',
            )}
          >
            {hdmiActive === true ? 'On' : hdmiActive === false ? 'Off' : '—'}
          </dd>
        </div>
        <div className="flex min-w-0 items-baseline gap-1.5">
          <dt className="shrink-0 text-muted-foreground">{'Now'}</dt>
          <dd className="min-w-0 truncate text-foreground" data-testid="testing-now-playing">
            {nowPlaying ? (
              <>
                {nowPlaying}
                {since ? <span className="text-muted-foreground"> · {since}</span> : null}
              </>
            ) : (
              '—'
            )}
          </dd>
        </div>
        <div className="flex items-center gap-1.5">
          <dt className="text-muted-foreground">{'Content'}</dt>
          <dd className="text-foreground">
            {contentExpired ? (
              <span className="font-medium text-amber-700 dark:text-amber-500">
                {'Content expired'}
              </span>
            ) : (
              'OK'
            )}
          </dd>
        </div>
      </dl>
      {onViewScreenshot ? (
        <Button
          variant="ghost"
          size="xs"
          onClick={onViewScreenshot}
          className="shrink-0 cursor-pointer text-muted-foreground"
          data-testid="testing-view-screenshot"
        >
          {'View screenshot'}
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}
